import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { TextField, IconButton, Box } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import websocketService from '../../services/websocketService';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  inputBox: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1)
  },
  textField: {
    flex: 1
  },
  sendButton: {
    color: theme.palette.primary.main,
    '&:disabled': {
      color: theme.palette.action.disabled
    }
  }
}));

const MessageInput = ({
  onSendMessage,
  disabled,
  conversationId,
  recipientUserId
}) => {
  const classes = useStyles();
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);

  const handleChange = (event) => {
    const value = event.target.value;
    setMessage(value);

    // Send typing indicator
    if (value && !isTyping && conversationId && recipientUserId) {
      setIsTyping(true);
      websocketService.sendTypingIndicator(
        conversationId,
        recipientUserId,
        true
      );
    }

    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set timeout to stop typing indicator
    const timeout = setTimeout(() => {
      if (conversationId && recipientUserId) {
        websocketService.sendTypingIndicator(
          conversationId,
          recipientUserId,
          false
        );
      }
      setIsTyping(false);
    }, 2000);

    setTypingTimeout(timeout);
  };

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
      setIsTyping(false);

      // Clear typing indicator
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      if (conversationId && recipientUserId) {
        websocketService.sendTypingIndicator(
          conversationId,
          recipientUserId,
          false
        );
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.inputBox}>
        <TextField
          className={classes.textField}
          multiline
          maxRows={4}
          variant="outlined"
          placeholder="Type a message..."
          value={message}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          inputProps={{
            maxLength: 5000
          }}
        />
        <IconButton
          className={classes.sendButton}
          onClick={handleSend}
          disabled={!message.trim() || disabled}>
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

MessageInput.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  conversationId: PropTypes.string,
  recipientUserId: PropTypes.string
};

MessageInput.defaultProps = {
  disabled: false
};

export default MessageInput;

